import { useAppBar } from "@/appBar/useAppBar";
import DbotEmptyStateIllustration from "@/assets/img/dbot_emptystate.svg";
import { isFailure, isOk, isPending, ok } from "@/common/common";
import { DecisionType } from "@/common/decisions";
import { DocumentType, getDocumentLabel, isDocumentType } from "@/common/documents";
import { Alert } from "@/components-v2/Alert";
import { DecisionAttachmentsPanel } from "@/components-v2/AttachmentsPanel";
import { Box, VBox } from "@/components-v2/Box";
import { Button, LinkButton } from "@/components-v2/Button";
import { ChangePermissionDialogButton } from "@/components-v2/ChangePermissionDialogButton";
import { Checkbox, CheckboxGroup } from "@/components-v2/Checkbox";
import { DetailsGroup } from "@/components-v2/Details";
import { Dialog } from "@/components-v2/Dialog";
import { DocumentPreview } from "@/components-v2/DocumentPreview";
import { DropdownMenu, DropdownMenuItems } from "@/components-v2/Dropdown";
import { EmptyState } from "@/components-v2/EmptyState";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { Icon } from "@/components-v2/Icon";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { HiddenForMobile, VisibleOnMobile } from "@/components-v2/Mobile";
import { Select } from "@/components-v2/Select";
import { SelectFilesButton } from "@/components-v2/SelectFilesButton";
import { ShowPreviewButton } from "@/components-v2/ShowPreviewButton";
import { Tabs } from "@/components-v2/Tabs";
import { Tag } from "@/components-v2/Tag";
import { TextInput } from "@/components-v2/TextInput";
import { ChangeOfAccountantView } from "@/dbot/accountant/accountantComponents";
import { AcquireOwnSharesPowerOfAttorneyView } from "@/dbot/acquireOwnSharesPowerOfAttorney/acquireOwnSharesPowerOfAttorneyComponents";
import { ApproveAnnualAccountView } from "@/dbot/approveAnnualAccount/approveAnnualAccountComponents";
import { ApproveAuditorRemunerationView } from "@/dbot/approveAuditorRemuneration/approveAuditorRemunerationComponents";
import { ApprovalOfCompanyBoardRemunerationView } from "@/dbot/approveCompanyBoardRemuneration/approveCompanyBoardRemunerationComponents";
import { ApproveNominationCommitteeRemunerationView } from "@/dbot/approveNominationCommitteeRemuneration/approveNominationCommitteeRemunerationComponents";
import { ArticlesOfAssociationView } from "@/dbot/articlesofassociation/articlesofassociationComponents";
import { AssignsNewCompanyBoardView } from "@/dbot/assignsNewCompanyBoard/assignsNewCompanyBoardComponents";
import {
  prepDecisionAttachmentsPanel,
  prepUploadDecisionAttachmentsButton,
  UploadedAttachmentProps,
} from "@/dbot/attachments";
import { ChangeOfAuditorView } from "@/dbot/auditor/auditorComponents";
import { BoardAssignsSignatureView } from "@/dbot/boardAssignsSignature/boardAssignsSignatureComponents";
import {
  BoardMeetingMinutesDetailsPanel,
  BoardMeetingMinutesView2,
} from "@/dbot/boardMeetingMinutes/boardMeetingMinutesComponents";
import { getCustomBoardMeetingPanels } from "@/dbot/boardMeetingMinutes/boardMeetingMinutesDomain";
import { CapitalIncreaseThroughBonusIssueView } from "@/dbot/capitalIncreaseThroughBonusIssue/capitalIncreaseThroughBonusIssueComponents";
import { ChangeArticlesOfAssociationView } from "@/dbot/changeArticlesOfAssociation/changeArticlesOfAssociationComponents";
import { ChangeBusinessPurposeView } from "@/dbot/changeBusinessPurpose/changeBusinessPurposeComponents";
import { ChangeCompanyBoardInArticlesOfAssociationView } from "@/dbot/changeCompanyBoardInArticlesOfAssociation/changeCompanyBoardInArticlesOfAssociationComponents";
import { ChangeOfAddressView } from "@/dbot/changeOfAddress/changeOfAddressComponents";
import { ChangeOfBusinessManagerView } from "@/dbot/changeOfBusinessManager/changeOfBusinessManagerComponents";
import { ChangeSignatureInArticlesOfAssociationView } from "@/dbot/changeSignatureInArticlesOfAssociation/changeSignatureInArticlesOfAssociationComponents";
import { AgendaActions } from "@/dbot/common/commonAgendaComponents";
import { DetailsSummaryList, EditDetailsPanel } from "@/dbot/common/commonComponents";
import {
  AddDocumentActions,
  addDocumentActions,
  AddDocumentState,
  addDocumentState,
  canAddBatchCompany,
  confirmRemoveDocumentDialogActions,
  copyDecisionSelectorActions,
  DbotEventDispatcher,
  decisionSelectorActions,
  decisionsRequiringCustomization,
  decisionsWithOwnAttachments,
  decisionToDetailsPanel,
  DetailsPanel,
  DetailsPanelRef,
  DocumentItem,
  ElectronicGeneralMeetingStep,
  FollowUpDetailsPanel,
  GeneralMeetingDetailsPanel,
  getCompanyDetailsPanels,
  getDecisionDetailsPanels,
  getDecisionName,
  getOpenNextGeneralMeetingDialogEvents,
  getOpenNextPanelEvent,
  getOpenPrevGeneralMeetingDialogEvents,
  getOpenPrevPanelEvent,
  getRemoveEventForPanel,
  isDocumentLocked,
  isDocumentSent,
  isRemovingDecision,
  OpenDetailsPanelEvent,
  prepCopyDecisionSelectorViewState,
  prepDecisionSelectorViewState,
  prepSelectBatchCompaniesCheckboxGroup,
  ProjectCompany,
  ProjectContext,
  ProjectControllerState,
  ProjectDocumentStatus,
  projectStatusLabels,
  removeBatchCompanyActions,
  removeDocumentAction,
  selectBatchCompaniesActions,
  selectBatchCompaniesEvent,
} from "@/dbot/common/commonDomain";
import {
  GeneralMeetingVoting,
  GeneralMeetingVotingType,
} from "@/dbot/common/commonGeneralMeetingDomain";
import { CompanyNameView } from "@/dbot/companyNameChange/companyNameChangeComponents";
import { ConversionOfSharesView } from "@/dbot/conversionOfShares/conversionOfSharesComponents";
import { ConvertibleLoanPowerOfAttorneyView } from "@/dbot/convertibleLoanPoA/convertibleLoanPoAComponents";
import {
  CustomDocumentDetailsPanel,
  CustomDocumentView,
} from "@/dbot/customDocument/customDocumentComponents";
import { getCustomDocumentPanels } from "@/dbot/customDocument/customDocumentDomain";
import { DissolveCompanyView } from "@/dbot/dissolveCompany/dissolveCompanyComponents";
import { DividendView } from "@/dbot/dividend/dividendComponents";
import { DividendPowerOfAttorneyView } from "@/dbot/dividendPowerOfAttorney/dividendPowerOfAttorneyComponents";
import {
  ElectionOfCompanyBoardSubProjectInfo,
  ElectionOfCompanyBoardView,
} from "@/dbot/ElectionOfCompanyBoard/electionOfCompanyBoardComponents";
import { ElectNominationCommitteeView } from "@/dbot/electNominationCommittee/electNominationCommitteeComponents";
import { EmploymentOfManagingDirectorView } from "@/dbot/employmentOfManagingDirector/employmentOfManagingDirectorComponents";
import { FreetextView } from "@/dbot/freetext/freetextComponents";
import { filterDecisionRequiringVoting } from "@/dbot/freetext/freetextDomain";
import {
  GeneralMeetingMinutesDetailsPanel,
  GeneralMeetingMinutesView,
} from "@/dbot/generalMeetingMinutes/generalMeetingMinutesComponents";
import {
  getGeneralMeetingCustomAgendaItems,
  getGeneralMeetingCustomPanels,
} from "@/dbot/generalMeetingMinutes/generalMeetingMinutesDomain";
import { GroupContributionByGeneralMeetingView } from "@/dbot/groupContributionByGeneralMeeting/groupContributionByGeneralMeetingComponents";
import {
  IncreaseOfShareCapitalHeadingAction,
  IncreaseOfShareCapitalView,
} from "@/dbot/increaseOfShareCapital/increaseOfShareCapitalComponents";
import { IncreaseShareCapitalPowerOfAttorneyView } from "@/dbot/increaseShareCapitalPowerOfAttorney/increaseShareCapitalPowerOfAttorneyComponents";
import { InsertCompanyBoardInArticlesOfAssociationView } from "@/dbot/insertCompanyBoardInArticlesOfAssociation/insertCompanyBoardInArticlesOfAssociationComponents";
import { InsertSignatureInArticlesOfAssociationView } from "@/dbot/insertSignatureInArticlesOfAssociation/insertSignatureInArticlesOfAssociationComponents";
import { InterimBalanceSheetView } from "@/dbot/interimBalanceSheet/interimBalanceSheetComponents";
import { LiquidateCompanyView } from "@/dbot/liquidateCompany/liquidateCompanyComponents";
import { MergerView } from "@/dbot/merger/mergerComponents";
import { NoAccountantFormView } from "@/dbot/noAccountant/noAccountantComponents";
import { NoManagingDirectorFormView } from "@/dbot/noManagingDirector/noManagingDirectorComponents";
import { ShareTransferConsentView } from "@/dbot/shareTransferConsent/shareTransferConsentComponents";
import { OptionGrantLetterView } from "@/dbot/optionGrantLetter/optionGrantLetterComponents";
import { OptionExerciseView } from "@/dbot/optionExercise/optionExerciseComponents";
import { NoAuditorFormView } from "@/dbot/noAuditor/noAuditorComponents";
import { NoBusinessManagerFormView } from "@/dbot/noBusinessManager/noBusinessManagerComponents";
import {
  NoticeOfBoardMeetingDetailsPanel,
  NoticeOfBoardMeetingView,
} from "@/dbot/noticeBoardMeeting/noticeBoardMeetingComponents";
import { getNoticeOfBoardMeetingCustomPanels } from "@/dbot/noticeBoardMeeting/noticeBoardMeetingDomain";
import {
  NoticeToGeneralMeetingDetailsPanel,
  NoticeToGeneralMeetingView,
} from "@/dbot/noticeGeneralMeeting/noticeGeneralMeetingComponents";
import { getNoticeToGeneralMeetingCustomPanels } from "@/dbot/noticeGeneralMeeting/noticeGeneralMeetingDomain";
import { ProcuraView } from "@/dbot/procura/procuraComponents";
import { WhatWouldYouLikeToDo } from "@/dbot/projectList/projectListComponents";
import { ReductionOfShareCapitalView } from "@/dbot/reductionOfShareCapital/reductionOfShareCapitalComponents";
import { RemoveCompanyBoardFromArticlesOfAssociationView } from "@/dbot/removeCompanyBoardFromArticlesOfAssociation/removeCompanyBoardFromArticlesOfAssociationComponents";
import { RemoveSignatureFromArticlesOfAssociationView } from "@/dbot/removeSignatureFromArticlesOfAssociation/removeSignatureFromArticlesOfAssociationComponents";
import { ReverseSplitShareView } from "@/dbot/reverseSplitShare/reverseSplitShareComponents";
import { SplitShareView } from "@/dbot/splitShare/splitShareComponents";
import {
  SubscriptionFormDetailsPanel,
  SubscriptionFormView2,
} from "@/dbot/subscriptionForm/subscriptionFormComponents";
import { getSubscriptionFormCustomPanels } from "@/dbot/subscriptionForm/subscriptionFormDomain";
import {
  FollowUpDecision,
  FollowUpDecisionDetailsPanel,
  FollowUpDecisionSummary,
  SummaryDocumentDialog,
  SummaryValidationMessages,
} from "@/dbot/summary/summaryComponents";
import { getActiveFollowUpPanels } from "@/dbot/summary/summaryDomain";
import { DCEvent, showError } from "@/events/eventsDomain";
import { ConversionModel } from "@/fileUpload/ConversionModel";
import { formatDateAndTime, formatTime } from "@/i18n/dateFormat";
import { TMessage } from "@/i18n/tt";
import {
  ApproveAgenda,
  AttendanceRegistration,
  ChoiceOfMeetingChair,
  Decision,
  GeneralMeetingAttendanceDialog,
  MeetingsDialogs,
} from "@/meetings/generalMeeting/AdminElectronicGeneralMeetingPage";
import {
  generalMeetingPanels,
  GeneralMeetingStatus,
  getMeetingStatusTag,
  mapToAttendanceData,
  mapToVotingData,
} from "@/meetings/generalMeeting/electronicGeneralMeetingDomain";
import { GeneralMeetingVoteType } from "@/meetings/generalMeeting/generalMeetingDomain";
import { AlertMessage } from "@/stores/AlertMessageStore";
import {
  useDispatch,
  useLanguage,
  useMessageStore,
  useRootStore,
  useTranslation,
} from "@/stores/RootStoreContext";
import { Markdown } from "@/text/Markdown";
import { next, prev, single } from "@/utils/arrays";
import { isToday, toDate } from "@/utils/Dates";
import { when } from "@/utils/functions";
import {
  openLinkInNewWindow,
  previewBoardMeetingForAdmin,
  previewGeneralMeetingForAdmin,
} from "@/utils/navigation";
import { fromQueryString } from "@/utils/queryParams";
import { isMobileView } from "@/utils/screen";
import { first, property } from "lodash";
import { observer } from "mobx-react";
import { FC, useEffect } from "react";
import { getNoticeToGmPanels } from "../common/commonBoardMeetingDomain";
import { IncorporationView } from "../incorporation/incorporationComponents";
import {
  DecisionAndForm,
  DocumentAndForm,
  documentSendingOrSigningStatusTag,
  documentStatusTag,
  getProjectDocumentTitle,
  getProjectMenuItems,
  getTabLabel,
  ProjectController,
  TabInPreview,
} from "./projectDomain";

export function ConflictMessage({ dispatch }: { dispatch: DbotEventDispatcher }) {
  const { t } = useTranslation();
  return (
    <span>
      {t("dbot.conflict.messageStart")}{" "}
      <a
        href={location.href}
        className={"text-inherit"}
        onClick={(e) => {
          e.preventDefault();
          dispatch({ type: "dbot/reload-project" });
        }}
      >
        {t("dbot.conflict.reload")}
      </a>{" "}
      {t("dbot.conflict.messageEnd")}
    </span>
  );
}

export function ConfirmDeleteProjectDialog({
  isBatchProject,
  isDeleting,
  onDelete,
  onCancel,
  alert,
}: {
  isBatchProject?: boolean;
  isDeleting?: boolean;
  onDelete: (() => void) | DCEvent[];
  onCancel: (() => void) | DCEvent[];
  alert?: AlertMessage;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      maxWidth={"sm"}
      title={{ key: isBatchProject ? "dbot.confirm-delete-project.title.batch" : "confirm-delete" }}
      isSubmitting={isDeleting}
      submitLabel={t("delete")}
      onSubmit={onDelete}
      onCancel={onCancel}
    >
      <VBox gap={"md"}>
        <Markdown
          value={t(
            isBatchProject ? "dbot.confirm-delete-project.batch" : "dbot.confirm-delete-project",
          )}
          enableLinks
        />
        {alert && <Alert {...alert} />}
      </VBox>
    </Dialog>
  );
}

const DocumentActions = observer(function DocumentActions({
  ctrl,
  document,
}: {
  ctrl: ProjectController;
  document: DocumentAndForm;
}) {
  const { t } = useTranslation();
  return ctrl.isLocked || isDocumentLocked(ctrl, document.documentId) ? null : (
    <DropdownMenu
      button={{ type: "dotdotdot" }}
      items={[
        {
          type: "item",
          icon: { name: "trash" },
          label: t("remove"),
          color: "error",
          onSelect: () => ctrl.dispatch(removeDocumentAction(document.documentId)),
        },
      ]}
    />
  );
});

export const DocumentSummary: FC<{
  context: { documentStatuses: ProjectDocumentStatus[] };
  document: DocumentAndForm;
  expanded?: boolean;
}> = observer(({ document, context, expanded }) => {
  const { t } = useTranslation();
  const lang = useLanguage();
  return (
    <Box alignItems="center" fontWeight={expanded ? "bold" : "normal"} testId={document.type}>
      {t(getProjectDocumentTitle(document, lang))}
      {document.type !== DocumentType.articlesofassociation && (
        <Tag {...documentStatusTag(context, document.documentId)} />
      )}
      {when(documentSendingOrSigningStatusTag(context, document.documentId), (props) => (
        <Tag {...props} />
      ))}
    </Box>
  );
});

const ConfirmRemoveAgendaItemDialog: FC<{
  ctrl: ProjectController;
}> = observer(({ ctrl }) => {
  const { t } = useTranslation();
  if (!ctrl.showConfirmRemoveDecisionDialog) {
    return null;
  }
  return (
    <Dialog
      title={t("dbot.confirmRemoveAgendaItem")}
      submitLabel={t("yes")}
      cancelLabel={t("no")}
      isSubmitting={isRemovingDecision(ctrl.state)}
      onSubmit={ctrl.confirmRemoveDecision}
      onCancel={ctrl.cancelRemoveDecision}
    />
  );
});

const ConfirmRemoveDocumentDialog: FC<{
  ctrl: ProjectController;
}> = observer(({ ctrl }) => {
  const { t } = useTranslation();
  if (ctrl.state.type !== "removeDocument") {
    return null;
  }
  const actions = confirmRemoveDocumentDialogActions(ctrl.state);
  return (
    <Dialog
      title={t("dbot.confirmRemoveDocument")}
      submitLabel={t("yes")}
      cancelLabel={t("no")}
      onSubmit={() => ctrl.dispatch(actions.onSubmit)}
      onCancel={() => ctrl.dispatch(actions.onCancel)}
    />
  );
});

export const SelectBatchCompaniesDialog: FC<{
  state: ProjectControllerState;
  dispatch: DbotEventDispatcher;
}> = observer(({ state, dispatch }) => {
  const { t } = useTranslation();
  if (state.type !== "select-batch-companies") {
    return null;
  }
  const { submit, cancel, setFilter, toggleAll } = selectBatchCompaniesActions(state);
  const checkboxGroup = prepSelectBatchCompaniesCheckboxGroup(state, dispatch);
  return (
    <Dialog
      title={t("dbot.select-batch-companies")}
      submitLabel={t("save")}
      isSubmitting={isPending(state.result)}
      onSubmit={() => dispatch(submit)}
      onCancel={() => dispatch(cancel)}
    >
      <VBox gap={"lg"}>
        <TextInput
          icon={"magnifying-glass"}
          placeholder={t("search")}
          value={state.filter ?? ""}
          onChange={(event) => dispatch(setFilter(event.target.value))}
        />

        <VBox alignItems="start">
          <LinkButton
            label={t("select-all-none")}
            onClick={() => dispatch(toggleAll(checkboxGroup.options))}
          />
          <CheckboxGroup {...checkboxGroup} />
        </VBox>
      </VBox>
    </Dialog>
  );
});

const RemoveBatchCompanyDialog: FC<{
  state: ProjectControllerState;
  context: ProjectContext;
  dispatch: DbotEventDispatcher;
}> = observer(({ state, context, dispatch }) => {
  const { t } = useTranslation();
  if (state.type !== "remove-batch-company") {
    return null;
  }
  const { submit, cancel } = removeBatchCompanyActions(state, context);
  return (
    <Dialog
      title={t("dbot.confirm-remove-batch-company")}
      submitLabel={t("confirm")}
      isSubmitting={isPending(state.result)}
      onSubmit={() => dispatch(submit)}
      onCancel={() => dispatch(cancel)}
    />
  );
});

const DocumentEmptyState = observer(function DocumentEmptyState({
  ctrl,
}: {
  ctrl: ProjectController;
}) {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={<img src={DbotEmptyStateIllustration} alt={"Generated documents"} />}
      title={t("dbot.no-documents.title")}
      description={t("dbot.no-documents.description")}
      actions={<AddDocumentContainer ctrl={ctrl} />}
    />
  );
});

function mapToUploadedAttachments(
  ctrl: ProjectController,
  current?: TabInPreview,
): UploadedAttachmentProps[] | undefined {
  if (!current || !("uploadedAttachments" in current)) {
    return undefined;
  }
  return current.uploadedAttachments?.map((a) => ({
    ...a,
    downloadUrl: `/api/project/${ctrl.projectId}/attachment/${a.id}`,
    downloadOriginalUrl: `/api/project/${ctrl.projectId}/attachment/${a.id}/original`,
  }));
}

const DocumentTabs: FC<{
  ctrl: ProjectController;
}> = observer(({ ctrl }) => {
  const { t } = useTranslation();
  const history = useRootStore().history;
  const urlParams = fromQueryString(history.location.search);
  const tabFromParameter = ctrl.documentTabs.find((tab) => tab.type === urlParams["previewTab"]);
  const current =
    tabFromParameter ??
    ctrl.documentTabs.find((p) => p.documentId === ctrl.selectedDocumentPreview) ??
    first(ctrl.documentTabs);
  return (
    <VBox gap={0} overflow={"hidden"} grow>
      <VBox container maxWidth={"document"} bg={"base"} paddingBottom={"sm"}>
        {!ctrl.isScreenShareMode && (
          <Tabs
            tabs={ctrl.documentTabs.map((x) => ({
              id: x.documentId,
              label: t(x.title),
              isActive: x.documentId === current?.documentId,
              onSelect: () =>
                ctrl.dispatch({
                  type: "dbot/toggle-document-panel",
                  documentId: x.documentId,
                  expanded: true,
                }),
            }))}
          />
        )}
        <div className={"flex-none"}>
          <Box alignItems={"center"} justifyContent={"space-between"}>
            {current?.companySelector ? (
              <Box>
                <Button
                  variant={"basic"}
                  icon={"caret-up"}
                  tooltip={t("previous")}
                  tooltipLabel={"Ctrl+↑"}
                  onClick={current?.prevCompany}
                  disabled={!current.prevCompany}
                />
                <Button
                  variant={"basic"}
                  icon={"caret-down"}
                  tooltip={t("next")}
                  tooltipLabel={"Ctrl+↓"}
                  onClick={current?.nextCompany}
                  disabled={!current.nextCompany}
                />
                <Select {...current.companySelector} />
              </Box>
            ) : (
              current?.recipientLabel && <div>{t(current.recipientLabel)}</div>
            )}
            {current?.recipientSelector ? (
              <Box>
                <Select {...current.recipientSelector} />
                <Button
                  variant={"basic"}
                  icon={"caret-left"}
                  tooltip={t("previous")}
                  tooltipLabel={"Ctrl+←"}
                  onClick={current?.prevRecipient}
                  disabled={!current.prevRecipient}
                />
                <Button
                  variant={"basic"}
                  icon={"caret-right"}
                  tooltip={t("next")}
                  tooltipLabel={"Ctrl+→"}
                  onClick={current?.nextRecipient}
                  disabled={!current.nextRecipient}
                />
              </Box>
            ) : current?.companyLabel ? (
              t(current.companyLabel)
            ) : undefined}
          </Box>
        </div>
      </VBox>
      <VBox grow overflowY="auto">
        {ctrl.documentTabs.length === 0 ? (
          <DocumentEmptyState ctrl={ctrl} />
        ) : (
          <DocumentPreview
            scrollToId={ctrl.scrollToId}
            marginTop={ctrl.documentTabs.length <= 1 || ctrl.isScreenShareMode}
            content={current?.content}
            uploadedAttachments={mapToUploadedAttachments(ctrl, current)}
            uploadingAttachments={
              current && "uploadingAttachments" in current
                ? current?.uploadingAttachments
                : undefined
            }
            isUpdating={ctrl.isSaving}
            download={current?.download}
            downloadAttachment={
              current && "downloadAttachment" in current ? current.downloadAttachment : undefined
            }
            upload={current?.upload}
            uploadFileConfig={current?.uploadFileConfig}
            isUploading={current?.isUploading}
            isConverting={current?.isConverting}
            extraAction={current?.extraAction}
            onConvert={(a) => {
              const conversion = new ConversionModel(
                ctrl.http,
                { type: "attachmentId", id: a.id },
                a.filename,
                ctrl.dispatch,
              );
              if (a.documentConversionId) {
                if (a.isConverting) {
                  conversion.checkStatusInBackground(false, undefined, a.documentConversionId);
                } else {
                  conversion.conversionResult = ok(a.documentConversionId);
                }
              }
              ctrl.dispatch({
                type: "preview-file",
                filename: a.filename,
                file: { url: a.downloadUrl! },
                conversion,
              });
            }}
          />
        )}
      </VBox>
    </VBox>
  );
});

const AddDocumentContainer = observer(({ ctrl }: { ctrl: ProjectController }) => {
  return (
    <AddDocument
      state={addDocumentState(ctrl.projectContext)}
      actions={addDocumentActions(ctrl.projectContext)}
      dispatch={ctrl.dispatch}
    />
  );
});

export const AddDocument = observer(function AddDocument({
  state,
  actions,
  dispatch,
}: {
  state: AddDocumentState;
  actions: AddDocumentActions;
  dispatch: DbotEventDispatcher;
}) {
  const { t } = useTranslation();

  if (state.isDisabled) {
    return null;
  }

  const documentItems: DropdownMenuItems = state.documentOptions.map((d) => ({
    type: "item",
    label: d.label,
    tag: d.tag,
    onSelect: () => dispatch(actions.submit(d.type, d.options)),
  }));

  return (
    <DropdownMenu
      button={{
        type: "link",
        icon: "plus",
        label: t("dbot.addDocument"),
        loading: state.isLoading,
      }}
      items={documentItems}
    />
  );
});

export const DecisionSelectorDialog = observer(({ ctrl }: { ctrl: ProjectController }) => {
  const { t } = useTranslation();
  const state = ctrl.state;

  if (state.type !== "decision-selector") {
    return null;
  }

  const dispatch = ctrl.dispatch;
  const context = ctrl.projectContext;
  const actions = decisionSelectorActions(state);
  const { decisionsByCategory } = prepDecisionSelectorViewState(state, context);

  return (
    <Dialog
      submitLabel={t("dbot.addSelectedToAgenda")}
      onSubmit={() => when(actions.submit, dispatch)}
      isSubmitDisabled={!actions.submit}
      isSubmitting={state.isLoading}
      onCancel={() => dispatch(actions.cancel)}
      fullscreen
      title={t("decisions.what-to-do")}
    >
      <WhatWouldYouLikeToDo
        decisionsByCategory={decisionsByCategory}
        toggleDecision={(decisionType) => dispatch(actions.toggle(decisionType))}
      />
    </Dialog>
  );
});

export const CopyDecisionSelectorDialog = observer(({ ctrl }: { ctrl: ProjectController }) => {
  const { t, lang } = useTranslation();
  const state = ctrl.state;

  if (state.type !== "copy-decision-selector") {
    return null;
  }

  const dispatch = ctrl.dispatch;
  const context = ctrl.projectContext;
  const actions = copyDecisionSelectorActions(state);
  const { projects, selectedProject, decisions } = prepCopyDecisionSelectorViewState(
    state,
    context,
  );
  return (
    <Dialog
      submitLabel={t("dbot.duplicate.decision.submit")}
      onSubmit={() => when(actions.submit, dispatch)}
      isSubmitDisabled={!actions.submit}
      isSubmitting={state.isLoading}
      onCancel={() => dispatch(actions.cancel)}
      padding={"lg"}
      title={t("dbot.duplicate.decision.title")}
      height={"md"}
    >
      {isPending(state.projects) && (
        <VBox grow={"golden"} justifyContent={"center"}>
          <LoadingIndicator />
        </VBox>
      )}
      {isFailure(state.projects) && (
        <VBox grow={"golden"} justifyContent={"center"}>
          <Alert message={{ key: "general-error" }} type={"error"} />
        </VBox>
      )}
      {isOk(state.projects) && (
        <VBox gap={"lg"} fullheight>
          {projects.length === 0 && (
            <VBox grow={"golden"} justifyContent={"center"}>
              <EmptyState
                illustration={
                  <Icon
                    name={"list-magnifying-glass"}
                    duotone={true}
                    weight={"light"}
                    size={"xxl"}
                  />
                }
                title={t("dbot.duplicate.decision.noProjects")}
              />
            </VBox>
          )}
          {projects.length > 0 && (
            <Box>
              <Select
                icon={"sparkle"}
                value={selectedProject}
                items={projects}
                onSelect={(item) =>
                  dispatch(
                    actions.setSelectedProject(
                      projects.findIndex((project) => project.projectId === item.projectId),
                    ),
                  )
                }
                idFn={(item) => item.projectId}
                labelFn={(item) =>
                  item.title.length > 71 ? item.title.slice(0, 70) + "..." : item.title
                }
              />
              {!isMobileView() && (
                <>
                  <Button
                    variant={"basic"}
                    icon={"caret-left"}
                    tooltip={t("previous")}
                    disabled={state.selectedProjectIndex <= 0}
                    onClick={() =>
                      dispatch(actions.setSelectedProject(state.selectedProjectIndex - 1))
                    }
                  />
                  <Button
                    variant={"basic"}
                    icon={"caret-right"}
                    tooltip={t("next")}
                    disabled={state.selectedProjectIndex >= projects.length - 1}
                    onClick={() =>
                      dispatch(actions.setSelectedProject(state.selectedProjectIndex + 1))
                    }
                  />
                </>
              )}
            </Box>
          )}
          {selectedProject && decisions.length === 0 && (
            <VBox grow={"golden"} justifyContent={"center"}>
              <EmptyState
                illustration={
                  <Icon
                    name={"list-magnifying-glass"}
                    duotone={true}
                    weight={"light"}
                    size={"xxl"}
                  />
                }
                title={t("dbot.duplicate.decision.noDecisions")}
              />
            </VBox>
          )}
          {decisions.length > 0 && (
            <VBox>
              <Box>
                <LinkButton
                  label={t("select-all")}
                  onClick={() =>
                    dispatch(
                      actions.toggleAll(
                        decisions
                          .filter((d) => !d.isDisabled && !d.isExisting)
                          .map((d) => d.decision.decisionId),
                      ),
                    )
                  }
                />
              </Box>
              {decisions.map(({ decision, isSelected, isDisabled, isExisting }) => {
                const freetextTitle =
                  decision.freetextTitles?.find((t) => t.language === lang)?.text ??
                  decision.freetextTitles?.[0]?.text ??
                  "";
                return (
                  <div key={decision.decisionId}>
                    <Checkbox
                      label={t(
                        decision.decisionType === DecisionType.freetext && freetextTitle
                          ? { translated: freetextTitle }
                          : getDecisionName(decision.decisionType),
                      )}
                      onChange={() => dispatch(actions.toggle(decision.decisionId))}
                      disabled={isDisabled || isExisting}
                      checked={isSelected}
                    />
                  </div>
                );
              })}
            </VBox>
          )}
        </VBox>
      )}
    </Dialog>
  );
});

export const ProjectTitle = observer(function ProjectTitle({
  ctrl,
  disableEdit,
}: {
  ctrl: ProjectController;
  disableEdit?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={0}>
      {disableEdit || ctrl.isLocked ? (
        <Box>
          <span>{ctrl.name}</span>
        </Box>
      ) : (
        <button onClick={() => !ctrl.isLocked && ctrl.dispatch({ type: "dbot/edit-project-name" })}>
          <Box alignItems={"center"}>
            <span>{ctrl.name}</span>
            <Icon name={"edit"} color="info" />
          </Box>
        </button>
      )}
      <Box>
        <small className={"subtle"} data-testid="project-status">
          {t(projectStatusLabels[ctrl.project.projectStatus])}
        </small>
      </Box>
    </VBox>
  );
});

const AddCompanyButton: FC<{ ctrl: ProjectController }> = observer(({ ctrl }) => {
  const { t } = useTranslation();
  return ctrl.isLocked || !canAddBatchCompany(ctrl.projectContext) ? null : (
    <Box>
      <LinkButton
        disabled={!canAddBatchCompany(ctrl.projectContext)}
        icon={"plus"}
        label={t("dbot.add-company")}
        onClick={() => ctrl.dispatch(selectBatchCompaniesEvent(ctrl.projectContext))}
      />
    </Box>
  );
});

const ProjectCompanies: FC<{ ctrl: ProjectController }> = observer(({ ctrl }) => {
  const { t } = useTranslation();
  return (
    <VBox paddingX gap={"lg"}>
      <VBox gap={"md"}>
        <DetailsSummaryList
          dispatch={ctrl.dispatch}
          panels={getCompanyDetailsPanels(ctrl.projectContext)}
          isLocked={ctrl.isLocked && ctrl.projectContext.isBatchProject}
        />
        <AddCompanyButton ctrl={ctrl} />
      </VBox>
      {ctrl.visibleTabs.includes("matters") ? (
        <Button
          variant={"primary"}
          label={t("dbot.continue-to-matters")}
          onClick={() => ctrl.dispatch({ type: "dbot/open-tab", tab: "matters" })}
          arrow={"right"}
        />
      ) : ctrl.visibleTabs.includes("documents") ? (
        <Button
          variant={"primary"}
          label={t("dbot.continue-to-documents")}
          onClick={() => ctrl.dispatch({ type: "dbot/open-tab", tab: "documents" })}
          arrow={"right"}
        />
      ) : null}
    </VBox>
  );
});

const ProjectAgenda = observer(function ProjectAgenda({ ctrl }: { ctrl: ProjectController }) {
  const { t } = useTranslation();
  return (
    <VBox paddingX gap={"lg"}>
      <VBox gap={"md"}>
        <DetailsSummaryList
          dispatch={ctrl.dispatch}
          panels={getDecisionDetailsPanels(ctrl.projectContext)}
          isLocked={ctrl.isLocked}
        />
        <AgendaActions
          isLocked={ctrl.isLocked}
          context={ctrl.projectContext}
          dispatch={ctrl.dispatch}
        />
        <AddCompanyButton ctrl={ctrl} />
      </VBox>
      <Button
        variant={"primary"}
        label={t("dbot.continue-to-documents")}
        onClick={() => ctrl.dispatch({ type: "dbot/open-tab", tab: "documents" })}
        arrow={"right"}
      />
    </VBox>
  );
});

const ProjectMeetings = observer(function ProjectMeetings({ ctrl }: { ctrl: ProjectController }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const locale = useLanguage();
  const meeting = ctrl.project.electronicGeneralMeeting;
  const electronicGMController = ctrl.electronicGMController;
  const d = ctrl.documents.find((d) => d.type === DocumentType.notice_general_meeting);
  const isNoticeSentOrLive =
    meeting?.noticeId !== undefined &&
    (isDocumentSent(ctrl, meeting.noticeId) || meeting?.status === GeneralMeetingStatus.STARTED);
  const panels = generalMeetingPanels(isNoticeSentOrLive, ctrl.projectContext, meeting);

  useEffect(() => {
    if (meeting) {
      electronicGMController.setupForLiveUpdates(ctrl.projectContext, meeting);
      return () => electronicGMController.unsubscribeFromGeneralMeetingMessages();
    }
  }, [electronicGMController, ctrl.projectContext, meeting]);

  useEffect(() => {
    ctrl.reloadGeneralMeeting();
  }, [ctrl]);

  const renderInfoMessage = () => {
    if (meeting?.status === GeneralMeetingStatus.FINISHED) {
      return t("meetings.general-meeting.info.meetingEnded", {
        time: formatTime(locale, meeting?.finishedAt),
      });
    } else if (meeting?.status === GeneralMeetingStatus.STARTED) {
      return isAllMattersResolved
        ? t("meetings.general-meeting.info.allMatterResolved")
        : t("meetings.general-meeting.info.meetingStarted", {
            time: formatTime(locale, meeting?.startedAt),
          });
    } else if (!isNoticeSentOrLive) {
      return (
        <>
          <LinkButton
            label={t("meetings.general-meeting.info.noticeNotSent.link")}
            onClick={() => ctrl.dispatch({ type: "dbot/open-tab", tab: "documents" })}
          />
          {t("meetings.general-meeting.info.noticeNotSent.label")}
        </>
      );
    } else {
      return t("meetings.general-meeting.info.noticeSent", {
        date: isMeetingToday
          ? t("today-at", {
              t: form?.context.generalMeetingFields.timeGeneralMeeting.validatedValue,
            })
          : formatDateAndTime(
              locale,
              form?.context.generalMeetingFields.dateGeneralMeeting.validatedValue,
              form?.context.generalMeetingFields.timeGeneralMeeting.validatedValue,
            ),
      });
    }
  };

  const canStartGeneralMeeting =
    meeting?.status === undefined || meeting?.status === GeneralMeetingStatus.PUBLISHED;
  const isAllMattersResolved =
    meeting?.registrationStatus === GeneralMeetingStatus.FINISHED &&
    meeting?.meetingChairStatus === GeneralMeetingStatus.FINISHED &&
    meeting?.approveAgendaStatus === GeneralMeetingStatus.FINISHED &&
    meeting?.decisionStatuses.every((d) => d.status === GeneralMeetingStatus.FINISHED);

  const form = d?.form.type === DocumentType.notice_general_meeting ? d.form : undefined;
  const isMeetingToday = isToday(
    toDate(form?.context.generalMeetingFields.dateGeneralMeeting.validatedValue ?? ""),
  );
  const isMessageSuccess =
    (meeting?.status === GeneralMeetingStatus.STARTED && isAllMattersResolved) ||
    meeting?.status === GeneralMeetingStatus.FINISHED;

  useEffect(() => {
    if (isFailure(electronicGMController.result)) {
      dispatch([showError(electronicGMController.result.error)]);
    }
  }, [dispatch, electronicGMController.result]);

  return (
    <VBox padding gap={"lg"}>
      <VBox>
        {!ctrl.isScreenShareMode && (
          <Box alignItems={"center"}>
            {t("meetings.general-meeting")}
            <Tag {...getMeetingStatusTag(meeting?.status)} />
          </Box>
        )}
        <Alert type={isMessageSuccess ? "success" : "info"} message={renderInfoMessage()} />
        <DetailsSummaryList
          dispatch={ctrl.dispatch}
          isLocked={!isNoticeSentOrLive}
          panels={panels}
        />
      </VBox>
      {(isNoticeSentOrLive || meeting?.status === GeneralMeetingStatus.FINISHED) && (
        <Button
          label={t("meetings.general-meeting.announcement.title")}
          variant={"secondary"}
          icon={"megaphone"}
          onClick={electronicGMController.openMeetingAnnouncementDialog}
        />
      )}
      {meeting?.status === GeneralMeetingStatus.STARTED && (
        <Button
          label={t("meetings.settings.end-meeting")}
          variant={isAllMattersResolved ? "primary" : "secondary"}
          disabled={!isNoticeSentOrLive}
          onClick={electronicGMController.openEndGeneralMeetingDialog}
        />
      )}
      {meeting?.status === GeneralMeetingStatus.FINISHED && (
        <Button
          label={t("meetings.reopen")}
          icon={"arrow-counter-clockwise"}
          variant={"secondary"}
          onClick={electronicGMController.reopenMeeting}
        />
      )}
      {canStartGeneralMeeting && (
        <Button
          variant={"primary"}
          label={t("start-general-meeting")}
          disabled={!isNoticeSentOrLive}
          onClick={electronicGMController.openStartGeneralMeetingDialog}
        />
      )}
      {meeting?.status !== GeneralMeetingStatus.FINISHED && (
        <>
          <Button
            icon={"file-plus"}
            label={t("general-meeting.attendance.powerOfAttorney.register")}
            variant={"secondary"}
            disabled={!isNoticeSentOrLive || isAllMattersResolved}
            onClick={() => electronicGMController.openRegisterPowerOfAttorneyDialog()}
          />
        </>
      )}
      {meeting?.status === GeneralMeetingStatus.PUBLISHED && (
        <Button
          label={t("meetings.settings")}
          variant={"secondary"}
          icon={"gear"}
          onClick={() => electronicGMController.openMeetingSettingsDialog()}
        />
      )}
      <MeetingsDialogs
        cancel={electronicGMController.closeMeetingDialog}
        submit={electronicGMController.submitMeetingDialog}
        dialogState={electronicGMController.dialogState}
      />
    </VBox>
  );
});

const ProjectCompanySelector = observer(function ProjectCompanySelector({
  ctrl,
  selected,
  requiringCustomization,
  onSelect,
}: {
  ctrl: ProjectController;
  selected: { orgNo?: string };
  requiringCustomization?: boolean;
  onSelect: (c: ProjectCompany) => void;
}) {
  const { t } = useTranslation();
  const companies = ctrl.projectContext.companies;
  const mainCompany = ctrl.projectContext.companies.find((c) => c.isMainCompany);
  return ctrl.projectContext.isBatchProject ? (
    <VBox>
      <Box>
        <Select
          id={"set-per-company"}
          items={companies}
          value={companies.find((c) => c.orgNo === selected?.orgNo)}
          onSelect={onSelect}
          idFn={property("orgNo")}
          labelFn={property("name")}
          captionFn={(item) =>
            !requiringCustomization && mainCompany?.orgNo === item.orgNo
              ? { key: "dbot.batchProject.primary-company" }
              : undefined
          }
        />
        <Button
          variant={"basic"}
          icon={"caret-up"}
          tooltip={t("previous")}
          onClick={() => when(prev(companies, selected), onSelect)}
        />
        <Button
          variant={"basic"}
          icon={"caret-down"}
          tooltip={t("next")}
          onClick={() => when(next(companies, selected), onSelect)}
        />
      </Box>
    </VBox>
  ) : null;
});

const BatchProjectEmptyState = observer(function ProjectCompanySelector({
  onClick,
  mainProjectCompany,
}: {
  onClick: () => void;
  mainProjectCompany: ProjectCompany;
}) {
  return (
    <VBox grow justifyContent={"center"}>
      <EmptyState
        illustration={"suitcaseSimple"}
        description={{
          key: "dbot.batchProject.emptyState",
          params: { companyName: mainProjectCompany.name },
        }}
        subtle
        actions={[
          {
            type: "button",
            key: "batchProjectSelectAll",
            props: {
              label: {
                key: "dbot.batchProject.emptyState.button",
                params: { companyName: mainProjectCompany.name },
              },
              variant: "secondary",
              onClick,
            },
          },
        ]}
      />
    </VBox>
  );
});

const ProjectDocuments = observer(function ProjectDocuments({ ctrl }: { ctrl: ProjectController }) {
  return (
    <>
      {when(single(ctrl.sortedDocuments), (document) => (
        <VBox paddingX paddingTop={ctrl.isDocumentLocked(document.documentId) ? "md" : "sm"}>
          <Box justifyContent={"space-between"} alignItems={"center"}>
            <DocumentSummary context={ctrl} document={document} />
            <DocumentActions ctrl={ctrl} document={document} />
          </Box>
          <VBox marginBottom={"lg"} gap={"md"}>
            <ProjectDocument documentId={document.documentId} ctrl={ctrl} />
            <SummaryValidationMessages
              decisions={ctrl.projectContext.currentDecisions}
              dispatch={ctrl.dispatch}
              isDocumentValid={!ctrl.invalidDocuments.has(document.documentId)}
              validationError={ctrl.summaryController.context.validationErrors}
              prekontrollFailure={ctrl.prekontrollFailure}
            />
          </VBox>
        </VBox>
      )) ?? (
        <DetailsGroup
          panels={ctrl.sortedDocuments.map((document) => ({
            caretPosition: "left",
            open: ctrl.isDocumentPanelExpanded(document.documentId),
            onToggle: (expanded) =>
              ctrl.dispatch({
                type: "dbot/toggle-document-panel",
                documentId: document.documentId,
                expanded,
              }),
            titleBoxProps: {
              paddingY: ctrl.isLocked || isDocumentLocked(ctrl, document.documentId) ? "md" : "sm",
            },
            title: (
              <DocumentSummary
                context={ctrl}
                document={document}
                expanded={ctrl.isDocumentPanelExpanded(document.documentId)}
              />
            ),
            actions: <DocumentActions ctrl={ctrl} document={document} />,
            content: (
              <VBox marginBottom={"md"} gap={"md"}>
                <ProjectDocument documentId={document.documentId} ctrl={ctrl} />
                <SummaryValidationMessages
                  decisions={ctrl.projectContext.currentDecisions}
                  dispatch={ctrl.dispatch}
                  validationError={ctrl.summaryController.context.validationErrors}
                  isDocumentValid={!ctrl.invalidDocuments.has(document.documentId)}
                  prekontrollFailure={ctrl.prekontrollFailure}
                />
              </VBox>
            ),
          }))}
        />
      )}
      <Box padding>
        <AddDocumentContainer ctrl={ctrl} />
      </Box>
    </>
  );
});

const ProjectFollowUp = observer(function ProjectFollowUp({ ctrl }: { ctrl: ProjectController }) {
  return (
    <div>
      <SummaryValidationMessages
        decisions={ctrl.projectContext.currentDecisions}
        dispatch={ctrl.dispatch}
        isDocumentValid={true}
        prekontrollFailure={ctrl.prekontrollFailure}
      />
      <VBox gap={"lg"}>
        {ctrl.summaryController.decisionsForApply.map((item) => (
          <VBox key={`${item.orgNo}/${item.decision.decisionId}`} paddingX>
            <FollowUpDecisionSummary
              item={item}
              companies={ctrl.projectContext.companies}
              includeCompanyName={ctrl.projectContext.isBatchProject}
            />
            <FollowUpDecision item={item} dispatch={ctrl.dispatch} />
          </VBox>
        ))}
      </VBox>
    </div>
  );
});

const MobileDocumentPreview = observer(function MobileDocumentPreview({
  ctrl,
}: {
  ctrl: ProjectController;
}) {
  const { t } = useTranslation();
  const history = useRootStore().history;
  const urlParams = fromQueryString(history.location.search);
  const tabFromParameter = ctrl.documentTabs.find((tab) => tab.type === urlParams["previewTab"]);
  const current =
    tabFromParameter ??
    ctrl.documentTabs.find((p) => p.documentId === ctrl.selectedDocumentPreview) ??
    first(ctrl.documentTabs);

  if (!ctrl.isMobileDocumentPreviewOpen) {
    return null;
  }

  return (
    <Dialog fullscreen>
      <VBox>
        <div>
          <LinkButton
            label={t("close.preview")}
            onClick={() => ctrl.dispatch({ type: "dbot/close-mobile-document-preview" })}
            arrow={"left"}
          />
        </div>
        <Tabs
          tabs={ctrl.documentTabs.map((x) => ({
            id: x.documentId,
            label: t(isDocumentType(x.type) ? getDocumentLabel(x.type) : x.type),
            isActive: x.documentId === current?.documentId,
            onSelect: () =>
              ctrl.dispatch({
                type: "dbot/toggle-document-panel",
                documentId: x.documentId,
                expanded: true,
              }),
          }))}
        />

        {ctrl.documentTabs.length === 0 ? (
          <DocumentEmptyState ctrl={ctrl} />
        ) : (
          <DocumentPreview padding={false} content={current?.content} />
        )}
      </VBox>
    </Dialog>
  );
});

export const ProjectDecision = observer(function ProjectDecision({
  decisionId,
  documentId,
  ctrl,
}: {
  decisionId: string;
  documentId?: string;
  ctrl: ProjectController;
}) {
  if (ctrl.editedDecision?.decisionId !== decisionId) {
    return null;
  }

  const render = (d: DecisionAndForm<DecisionType>, documentContext: DocumentItem | undefined) => {
    switch (d.form.type) {
      case DecisionType.approveAnnualAccount:
        return <ApproveAnnualAccountView form={d.form} />;
      case DecisionType.approveAuditorRemuneration:
        return <ApproveAuditorRemunerationView form={d.form} />;
      case DecisionType.approveCompanyBoardRemuneration:
        return <ApprovalOfCompanyBoardRemunerationView form={d.form} />;
      case DecisionType.approveNominationCommitteeRemuneration:
        return <ApproveNominationCommitteeRemunerationView form={d.form} />;
      case DecisionType.assignsNewCompanyBoard:
        return <AssignsNewCompanyBoardView form={d.form} />;
      case DecisionType.electionOfCompanyBoard:
        return <ElectionOfCompanyBoardView form={d.form} />;
      case DecisionType.auditor:
        return <ChangeOfAuditorView form={d.form} />;
      case DecisionType.accountant:
        return <ChangeOfAccountantView form={d.form} />;
      case DecisionType.boardAssignsSignature:
        return <BoardAssignsSignatureView form={d.form} />;
      case DecisionType.capitalIncreaseThroughBonusIssue:
        return <CapitalIncreaseThroughBonusIssueView form={d.form} />;
      case DecisionType.changeBusinessPurpose:
        return <ChangeBusinessPurposeView form={d.form} />;
      case DecisionType.changeOfAddress:
        return <ChangeOfAddressView form={d.form} />;
      case DecisionType.changeOfBusinessManager:
        return <ChangeOfBusinessManagerView form={d.form} />;
      case DecisionType.changeCompanyBoardInArticlesOfAssociation:
        return <ChangeCompanyBoardInArticlesOfAssociationView form={d.form} />;
      case DecisionType.changeSignatureInArticlesOfAssociation:
        return <ChangeSignatureInArticlesOfAssociationView form={d.form} />;
      case DecisionType.companyName:
        return <CompanyNameView form={d.form} />;
      case DecisionType.convertibleLoanPowerOfAttorney:
        return <ConvertibleLoanPowerOfAttorneyView form={d.form} />;
      case DecisionType.dividend:
        return <DividendView form={d.form} />;
      case DecisionType.dividendPowerOfAttorney:
        return <DividendPowerOfAttorneyView form={d.form} />;
      case DecisionType.electNominationCommittee:
        return <ElectNominationCommitteeView form={d.form} />;
      case DecisionType.employmentOfManagingDirector:
        return <EmploymentOfManagingDirectorView form={d.form} />;
      case DecisionType.freetext:
        return <FreetextView form={d.form} documentContext={documentContext} />;
      case DecisionType.groupContributionByGeneralMeeting:
        return <GroupContributionByGeneralMeetingView form={d.form} />;
      case DecisionType.increaseOfShareCapital:
        return (
          <IncreaseOfShareCapitalView
            form={d.form}
            showOpenDetailsButton={Boolean(documentContext) && !ctrl.detailsPanelMaximized}
            openDetails={() => ctrl.dispatch({ type: "dbot/toggle-maximize-details-panel" })}
            isDetailsOpen={ctrl.detailsPanelMaximized}
            hasValidationError={ctrl.invalidDecisions.has(d.decisionId)}
          />
        );
      case DecisionType.increaseShareCapitalPowerOfAttorney:
        return <IncreaseShareCapitalPowerOfAttorneyView form={d.form} />;
      case DecisionType.insertCompanyBoardInArticlesOfAssociation:
        return <InsertCompanyBoardInArticlesOfAssociationView form={d.form} />;
      case DecisionType.insertSignatureInArticlesOfAssociation:
        return <InsertSignatureInArticlesOfAssociationView form={d.form} />;
      case DecisionType.interimBalanceSheet:
        return <InterimBalanceSheetView form={d.form} />;
      case DecisionType.dissolveCompany:
        return <DissolveCompanyView form={d.form} />;
      case DecisionType.liquidateCompany:
        return <LiquidateCompanyView form={d.form} />;
      case DecisionType.procura:
        return <ProcuraView form={d.form} />;
      case DecisionType.reductionOfShareCapital:
        return <ReductionOfShareCapitalView form={d.form} />;
      case DecisionType.removeCompanyBoardFromArticlesOfAssociation:
        return <RemoveCompanyBoardFromArticlesOfAssociationView form={d.form} />;
      case DecisionType.removeSignatureFromArticlesOfAssociation:
        return <RemoveSignatureFromArticlesOfAssociationView form={d.form} />;
      case DecisionType.reverseSplitShare:
        return <ReverseSplitShareView form={d.form} />;
      case DecisionType.splitShare:
        return <SplitShareView form={d.form} />;
      case DecisionType.noAuditor:
        return <NoAuditorFormView form={d.form} />;
      case DecisionType.noAccountant:
        return <NoAccountantFormView form={d.form} />;
      case DecisionType.noManagingDirector:
        return <NoManagingDirectorFormView form={d.form} />;
      case DecisionType.shareTransferConsent:
        return <ShareTransferConsentView form={d.form} />;
      case DecisionType.optionGrantLetter:
        return <OptionGrantLetterView form={d.form} />;
      case DecisionType.optionExercise:
        return <OptionExerciseView form={d.form} />;
      case DecisionType.noBusinessManager:
        return <NoBusinessManagerFormView form={d.form} />;
      case DecisionType.changeArticlesOfAssociation:
        return <ChangeArticlesOfAssociationView form={d.form} />;
      case DecisionType.conversionOfShares:
        return <ConversionOfSharesView form={d.form} />;
      case DecisionType.acquireOwnSharesPowerOfAttorney:
        return <AcquireOwnSharesPowerOfAttorneyView form={d.form} />;
      case DecisionType.incorporation:
        return <IncorporationView form={d.form} />;
      case DecisionType.merger:
        return <MergerView form={d.form} />;
    }
  };

  const renderBatchDecision = (d: DecisionAndForm<DecisionType>, ctrl: ProjectController) => {
    const mainProjectCompany = ctrl.projectContext.companies.find((c) => c.isMainCompany)!;
    const isMainProject = ctrl.editedDecision?.orgNo === mainProjectCompany.orgNo;

    switch (d.form.type) {
      case DecisionType.electionOfCompanyBoard:
        return (
          <>
            <ElectionOfCompanyBoardView form={d.form} />
            {!isMainProject && (
              <ElectionOfCompanyBoardSubProjectInfo
                mainProjectCompany={mainProjectCompany}
                onActionClick={() => ctrl.dispatch(openDetailsEvent(mainProjectCompany.orgNo))}
              />
            )}
          </>
        );
      default:
        return decisionsRequiringCustomization.has(d.form.type) || isMainProject ? (
          render(
            d,
            ctrl.projectContext.currentDocuments.find((d) => d.documentId === documentId),
          )
        ) : (
          <BatchProjectEmptyState
            onClick={() => ctrl.dispatch(openDetailsEvent(mainProjectCompany.orgNo))}
            mainProjectCompany={mainProjectCompany}
          />
        );
    }
  };

  const openDetailsEvent = (orgNo?: string): OpenDetailsPanelEvent => {
    return {
      type: "dbot/open-details-panel",
      panel: {
        type: "decision",
        decisionId,
        documentId,
        orgNo: orgNo,
      },
    };
  };

  return (
    <>
      <ProjectCompanySelector
        ctrl={ctrl}
        onSelect={(company) => {
          ctrl.dispatch(openDetailsEvent(company?.orgNo));
        }}
        requiringCustomization={decisionsRequiringCustomization.has(
          ctrl.editedDecision.decisionType,
        )}
        selected={{ orgNo: ctrl.editedDecision.orgNo }}
      />
      {!ctrl.projectContext.isBatchProject
        ? render(
            ctrl.editedDecision.decision,
            ctrl.projectContext.currentDocuments.find((d) => d.documentId === documentId),
          )
        : renderBatchDecision(ctrl.editedDecision.decision, ctrl)}
    </>
  );
});

export const ProjectDecisionHeadingAction = observer(({ ctrl }: { ctrl: ProjectController }) => {
  const form = ctrl.editedDecision?.decision.form;

  switch (form?.type) {
    case DecisionType.increaseOfShareCapital:
      return <IncreaseOfShareCapitalHeadingAction form={form} />;
    default:
      return null;
  }
});

export const ProjectDecisionAttachment = observer(function ProjectDecision({
  decisionId,
  ctrl,
}: {
  decisionId: string;
  documentId?: string;
  ctrl: ProjectController;
}) {
  if (
    ctrl.editedDecision?.decisionId !== decisionId ||
    decisionsWithOwnAttachments.has(ctrl.editedDecision.decision.type)
  ) {
    return null;
  }

  return (
    <>
      {when(
        prepDecisionAttachmentsPanel(
          ctrl.projectContext,
          ctrl.editedDecision.decision.decisionId,
          ctrl.editedDecision.decision.attachments,
          ctrl.dispatch,
        ),
        (props) => (
          <DecisionAttachmentsPanel {...props} />
        ),
      )}
      {when(
        prepUploadDecisionAttachmentsButton(
          ctrl.projectContext,
          ctrl.editedDecision.decision.decisionId,
          ctrl.dispatch,
        ),
        (props) => (
          <Box>
            <SelectFilesButton {...props} />
          </Box>
        ),
      )}
    </>
  );
});

const ProjectDocument = observer(function ProjectDocument({
  documentId,
  ctrl,
}: {
  documentId: string;
  ctrl: ProjectController;
}) {
  const d = ctrl.documents.find((d) => d.documentId === documentId);
  if (!d) {
    return null;
  }
  switch (d.form.type) {
    case DocumentType.notice_board_meeting:
      return <NoticeOfBoardMeetingView form={d.form} />;
    case DocumentType.board_meeting:
      return <BoardMeetingMinutesView2 form={d.form} />;
    case DocumentType.notice_general_meeting:
      return <NoticeToGeneralMeetingView form={d.form} />;
    case DocumentType.general_meeting:
      return <GeneralMeetingMinutesView form={d.form} />;
    case DocumentType.subscription_form:
      return <SubscriptionFormView2 form={d.form} />;
    case DocumentType.articlesofassociation:
      return <ArticlesOfAssociationView form={d.form} />;
    case DocumentType.custom_document:
      return <CustomDocumentView form={d.form} />;
  }
});

const ProjectDocumentDetailsPanel = observer(function ProjectDocumentDetailsPanel({
  id,
  documentId,
  ctrl,
}: {
  id: string;
  documentId: string;
  ctrl: ProjectController;
}) {
  const d = ctrl.documents.find((d) => d.documentId === documentId);
  if (!d) {
    return null;
  }

  const renderDecisionForm = (decisionId: string) => (
    <ProjectDecision decisionId={decisionId} documentId={documentId} ctrl={ctrl} />
  );

  const renderDecisionAttachment = (decisionId: string) => (
    <ProjectDecisionAttachment decisionId={decisionId} ctrl={ctrl} />
  );

  const getPanels = (): DetailsPanel[] => {
    switch (d.form.type) {
      case DocumentType.notice_board_meeting:
        return [
          ...getNoticeOfBoardMeetingCustomPanels(d.form),
          ...getDecisionDetailsPanels({
            documentId,
            currentDecisions: ctrl.projectContext.currentDecisions,
          }),
          ...getNoticeToGmPanels(d.form.context),
        ];
      case DocumentType.board_meeting:
        return [
          ...getCustomBoardMeetingPanels(d.form),
          ...getDecisionDetailsPanels({
            documentId,
            currentDecisions: ctrl.projectContext.currentDecisions,
          }),
          ...getNoticeToGmPanels(d.form.context),
        ];
      case DocumentType.notice_general_meeting:
        return [
          ...getNoticeToGeneralMeetingCustomPanels(d.form),
          ...getDecisionDetailsPanels({
            documentId,
            currentDecisions: ctrl.projectContext.currentDecisions,
          }),
        ];
      case DocumentType.general_meeting:
        return [
          ...getGeneralMeetingCustomPanels(d.form),
          ...getGeneralMeetingCustomAgendaItems(d.form),
          ...getDecisionDetailsPanels({
            documentId,
            currentDecisions: ctrl.projectContext.currentDecisions,
          }),
        ];
      case DocumentType.subscription_form:
        return getSubscriptionFormCustomPanels(d.form);
      case DocumentType.custom_document:
        return getCustomDocumentPanels(d.form);
      default:
        return [];
    }
  };

  const panels = getPanels();
  const panel = panels.find((p) => p.id === id);

  const getPanelTitle = (): TMessage | undefined => {
    switch (d.form.type) {
      case DocumentType.notice_board_meeting:
        return { key: "dbot.edit-notice-of-board-meeting" };
      case DocumentType.board_meeting:
        return { key: "dbot.edit-board-minutes" };
      case DocumentType.notice_general_meeting:
        return { key: "dbot.edit-notice-to-general-meeting" };
      case DocumentType.general_meeting:
        return { key: "dbot.edit-general-meeting-minutes" };
      case DocumentType.subscription_form:
        return { key: "dbot.edit-subscription-form" };
      case DocumentType.custom_document:
        return {
          key: "dbot.edit-custom-document",
          params: { title: getProjectDocumentTitle(d, ctrl.projectContext.locale) },
        };
    }
  };

  const render = () => {
    if (!panel) {
      return null;
    }
    switch (d.form.type) {
      case DocumentType.notice_board_meeting:
        return (
          <NoticeOfBoardMeetingDetailsPanel
            panel={panel}
            form={d.form}
            renderDecisionForm={renderDecisionForm}
            renderDecisionAttachment={renderDecisionAttachment}
          />
        );
      case DocumentType.board_meeting:
        return (
          <BoardMeetingMinutesDetailsPanel
            panel={panel}
            form={d.form}
            renderDecisionForm={renderDecisionForm}
            renderDecisionAttachment={renderDecisionAttachment}
          />
        );
      case DocumentType.notice_general_meeting:
        return (
          <NoticeToGeneralMeetingDetailsPanel
            panel={panel}
            form={d.form}
            renderDecisionForm={renderDecisionForm}
            renderDecisionAttachment={renderDecisionAttachment}
          />
        );
      case DocumentType.general_meeting:
        return (
          <GeneralMeetingMinutesDetailsPanel
            panel={panel}
            form={d.form}
            renderDecisionForm={renderDecisionForm}
            renderDecisionAttachment={renderDecisionAttachment}
          />
        );
      case DocumentType.subscription_form:
        return <SubscriptionFormDetailsPanel panel={panel} form={d.form} />;
      case DocumentType.custom_document:
        return <CustomDocumentDetailsPanel panel={panel} form={d.form} />;
    }
  };

  return (
    <EditDetailsPanel
      dispatch={ctrl.dispatch}
      title={getPanelTitle()}
      heading={panel?.heading}
      content={render()}
      remove={getRemoveEventForPanel(panel, ctrl)}
      next={getOpenNextPanelEvent(panel?.id, panels)}
      prev={getOpenPrevPanelEvent(panel?.id, panels)}
      toggleMaximized={{ type: "dbot/toggle-maximize-details-panel" }}
      isMaximized={ctrl.detailsPanelMaximized}
    />
  );
});

const ProjectDecisionDetailsPanel = observer(function ProjectDecisionDetailsPanel({
  decisionId,
  ctrl,
}: {
  decisionId: string;
  ctrl: ProjectController;
}) {
  const panels = ctrl.projectContext.currentDecisions.map((d) => decisionToDetailsPanel(d));
  const panel = panels.find((p) => p.id === decisionId);
  if (!panel) {
    return null;
  }

  return (
    <EditDetailsPanel
      dispatch={ctrl.dispatch}
      title={{ key: "dbot.edit-matter" }}
      heading={panel.heading}
      headingAction={<ProjectDecisionHeadingAction ctrl={ctrl} />}
      content={
        <>
          <ProjectDecision decisionId={panel.id} ctrl={ctrl} />
          <ProjectDecisionAttachment decisionId={panel.id} ctrl={ctrl} />
        </>
      }
      remove={getRemoveEventForPanel(panel, ctrl)}
      next={getOpenNextPanelEvent(panel.id, panels)}
      prev={getOpenPrevPanelEvent(panel.id, panels)}
      toggleMaximized={{ type: "dbot/toggle-maximize-details-panel" }}
      isMaximized={ctrl.detailsPanelMaximized}
    />
  );
});

const ProjectFollowUpDetailsPanel = observer(function ProjectFollowUpDetailsPanel({
  panel,
  ctrl,
}: {
  panel: FollowUpDetailsPanel;
  ctrl: ProjectController;
}) {
  const item = ctrl.summaryController.decisionsForApply.find(
    (d) => d.orgNo === panel.orgNo && d.decision.decisionId === panel.decisionId,
  );
  return !item ? null : (
    <FollowUpDecisionDetailsPanel
      dispatch={ctrl.dispatch}
      panel={panel}
      item={item}
      context={ctrl.projectContext}
      subscribe={ctrl.addEventListener}
    />
  );
});

const ProjectCompanyDetailsPanel: FC<{
  orgNo: string;
  ctrl: ProjectController;
}> = observer(({ orgNo, ctrl }) => {
  const panels = getCompanyDetailsPanels(ctrl.projectContext);
  const panel = panels.find((p) => p.id === orgNo);
  if (!panel) {
    return null;
  }

  return (
    <EditDetailsPanel
      dispatch={ctrl.dispatch}
      title={{ key: "dbot.edit-company" }}
      heading={panel.heading}
      subHeading={panel.label}
      content={<div />}
      remove={getRemoveEventForPanel(panel, ctrl)}
      next={getOpenNextPanelEvent(panel.id, panels)}
      prev={getOpenPrevPanelEvent(panel.id, panels)}
      toggleMaximized={{ type: "dbot/toggle-maximize-details-panel" }}
      isMaximized={ctrl.detailsPanelMaximized}
    />
  );
});

export const ProjectDetailsPanel = observer(function ProjectDetailsPanel({
  panel,
  ctrl,
}: {
  panel: DetailsPanelRef;
  ctrl: ProjectController;
}) {
  switch (panel.type) {
    case "decision":
      if (panel.documentId) {
        return (
          <ProjectDocumentDetailsPanel
            id={panel.decisionId}
            documentId={panel.documentId}
            ctrl={ctrl}
          />
        );
      } else {
        return <ProjectDecisionDetailsPanel decisionId={panel.decisionId} ctrl={ctrl} />;
      }
    case "custom":
      if (panel.documentId) {
        return (
          <ProjectDocumentDetailsPanel id={panel.id} documentId={panel.documentId} ctrl={ctrl} />
        );
      } else {
        return null;
      }
    case "generalmeeting":
      return <ElectronicGeneralMeetingDetailsPanel panel={panel} ctrl={ctrl} />;
    case "follow-up":
      return <ProjectFollowUpDetailsPanel panel={panel} ctrl={ctrl} />;
    case "company":
      return <ProjectCompanyDetailsPanel orgNo={panel.orgNo} ctrl={ctrl} />;
  }
});

const generalMeetingHeading: { [key in ElectronicGeneralMeetingStep["type"]]: TMessage } = {
  registration: { key: "general-meeting.register" },
  choiceOfMeetingChair: { key: "documents.generalMeeting.choiceOfMeetingChair" },
  approveAgenda: { key: "documents.generalMeeting.approveAgenda" },
  decision: { key: "dbot.decision" },
};

const ElectronicGeneralMeetingDetailsPanel = observer(
  function ElectronicGeneralMeetingDetailsPanel({
    panel,
    ctrl,
  }: {
    panel: GeneralMeetingDetailsPanel;
    ctrl: ProjectController;
  }) {
    const d = ctrl.documents.find((d) => d.type === DocumentType.notice_general_meeting);
    if (!d) {
      return null;
    }
    const isNoticeSent = isDocumentSent(ctrl, d.documentId);

    const panels = generalMeetingPanels(
      isNoticeSent,
      ctrl.projectContext,
      ctrl.project.electronicGeneralMeeting,
    );
    const decisionId = panel.step.type === "decision" && panel.step.decisionId;
    const decision = decisionId
      ? ctrl.projectContext.currentDecisions.find((d) => d.decisionId === decisionId)
      : undefined;
    const onSaveVoting =
      (voteType: GeneralMeetingVoteType, decisionId?: string) => (voting: GeneralMeetingVoting) =>
        ctrl.dispatch({
          type: "dbot/update-voting-in-general-meeting",
          voteType,
          voting,
          decisionId,
        });
    const onSaveVotingType =
      (voteType: GeneralMeetingVoteType, decisionId?: string) =>
      (votingType?: GeneralMeetingVotingType) =>
        ctrl.dispatch({
          type: "dbot/update-voting-type-in-general-meeting",
          voteType,
          votingType,
          decisionId,
        });

    const render = (step: ElectronicGeneralMeetingStep) => {
      switch (step.type) {
        case "registration": {
          return (
            <AttendanceRegistration
              projectId={ctrl.projectId}
              dispatch={ctrl.dispatch}
              context={ctrl.projectContext}
              meeting={ctrl.project.electronicGeneralMeeting!}
              data={mapToAttendanceData(
                ctrl.projectContext.initialState,
                ctrl.project.electronicGeneralMeeting!,
              )}
              controller={ctrl.electronicGMController}
              isNoticeSent={isNoticeSent}
              next={getOpenNextGeneralMeetingDialogEvents(panel.id, panels)}
            />
          );
        }
        case "choiceOfMeetingChair":
          return (
            <ChoiceOfMeetingChair
              projectId={ctrl.projectId}
              dispatch={ctrl.dispatch}
              meeting={ctrl.project.electronicGeneralMeeting!}
              data={mapToVotingData(
                ctrl.projectContext.initialState,
                ctrl.project.electronicGeneralMeeting!,
                ctrl.project.electronicGeneralMeeting?.votes?.meetChair ?? [],
              )}
              controller={ctrl.electronicGMController}
              user={ctrl.projectContext.user}
              onSaveVoting={onSaveVoting(GeneralMeetingVoteType.meetChair)}
              isNoticeSent={isNoticeSent}
              next={getOpenNextGeneralMeetingDialogEvents(panel.id, panels)}
              prev={getOpenPrevGeneralMeetingDialogEvents(panel.id, panels)}
            />
          );
        case "approveAgenda":
          return (
            <ApproveAgenda
              projectId={ctrl.projectId}
              dispatch={ctrl.dispatch}
              meeting={ctrl.project.electronicGeneralMeeting!}
              data={mapToVotingData(
                ctrl.projectContext.initialState,
                ctrl.project.electronicGeneralMeeting!,
                ctrl.project.electronicGeneralMeeting?.votes?.approveNoticeAndAgenda ?? [],
              )}
              controller={ctrl.electronicGMController}
              user={ctrl.projectContext.user}
              onSaveVoting={onSaveVoting(GeneralMeetingVoteType.approveNoticeAndAgenda)}
              isNoticeSent={isNoticeSent}
              next={getOpenNextGeneralMeetingDialogEvents(panel.id, panels)}
              prev={getOpenPrevGeneralMeetingDialogEvents(panel.id, panels)}
            />
          );
        case "decision":
          return (
            <Decision
              projectId={ctrl.projectId}
              title={decision!.title}
              decisionId={step.decisionId}
              dispatch={ctrl.dispatch}
              meeting={ctrl.project.electronicGeneralMeeting!}
              data={mapToVotingData(
                ctrl.projectContext.initialState,
                ctrl.project.electronicGeneralMeeting!,
                ctrl.project.electronicGeneralMeeting?.votes?.decisions[step.decisionId] ?? [],
              )}
              controller={ctrl.electronicGMController}
              defaultVotingType={decision!.defaultGeneralMeetingVotingType}
              isApproved={decision!.isApprovedByGeneralMeeting}
              user={ctrl.projectContext.user}
              onSaveVoting={onSaveVoting(GeneralMeetingVoteType.decision, step.decisionId)}
              onSaveVotingType={onSaveVotingType(GeneralMeetingVoteType.decision, step.decisionId)}
              isNoticeSent={isNoticeSent}
              decisionMatters={filterDecisionRequiringVoting(
                ctrl.projectContext.currentDecisions,
                DocumentType.general_meeting,
              )}
              next={getOpenNextGeneralMeetingDialogEvents(panel.id, panels)}
              prev={getOpenPrevGeneralMeetingDialogEvents(panel.id, panels)}
            />
          );
      }
    };

    return (
      <EditDetailsPanel
        dispatch={ctrl.dispatch}
        title={{ key: "documents.generalMeeting.conductElectronicMeeting.tab.title" }}
        heading={decision?.title ?? generalMeetingHeading[panel.step.type]}
        content={() => render(panel.step)}
        next={getOpenNextPanelEvent(panel.id, panels)}
        prev={getOpenPrevPanelEvent(panel.id, panels)}
      />
    );
  },
);

export const ProjectDialogs = observer(({ ctrl }: { ctrl: ProjectController }) => {
  const { t } = useTranslation();
  return (
    <>
      {ctrl.state.type === "loading" && (
        <Dialog>
          <LoadingIndicator label={t(ctrl.state.message)} />
        </Dialog>
      )}
      <ConfirmRemoveAgendaItemDialog ctrl={ctrl} />
      <ConfirmRemoveDocumentDialog ctrl={ctrl} />
      {ctrl.state.type === "delete-project" && (
        <ConfirmDeleteProjectDialog
          isDeleting={isPending(ctrl.state.deleteResult)}
          isBatchProject={ctrl.projectContext.isBatchProject}
          onDelete={() => ctrl.dispatch({ type: "dbot/confirm-delete-project" })}
          onCancel={() => ctrl.dispatch({ type: "dbot/cancel-delete-project" })}
          alert={ctrl.state.alert}
        />
      )}
      <SummaryDocumentDialog
        state={ctrl.summaryController.dialogState}
        closeDialog={ctrl.summaryController.closeDialog}
        ctrl={ctrl}
      />
      {!ctrl.electronicGMController.showRegistrationDialog && (
        <GeneralMeetingAttendanceDialog
          dialogState={ctrl.electronicGMController.attendanceDialogState}
        />
      )}
      <SelectBatchCompaniesDialog state={ctrl.state} dispatch={ctrl.dispatch} />
      <RemoveBatchCompanyDialog
        state={ctrl.state}
        context={ctrl.projectContext}
        dispatch={ctrl.dispatch}
      />
      <DecisionSelectorDialog ctrl={ctrl} />
      <CopyDecisionSelectorDialog ctrl={ctrl} />
    </>
  );
});

export const ProjectView: FC<{
  ctrl: ProjectController;
}> = observer(({ ctrl }) => {
  const { t } = useTranslation();
  const appBar = useAppBar();
  const alertStore = useMessageStore();
  return (
    <>
      <FullScreenLayout
        appBar={!ctrl.isScreenShareMode && !ctrl.isOverlayMode && appBar}
        alertStore={alertStore}
        pageHeader={
          !ctrl.isScreenShareMode && {
            title: <ProjectTitle ctrl={ctrl} />,
            isMaximized: ctrl.isFullscreen,
            onToggleMaximize: ctrl.isOverlayMode
              ? undefined
              : () => ctrl.dispatch({ type: "dbot/toggle-fullscreen" }),
            menu: (
              <Box alignItems={"center"} gap={"lg"}>
                {ctrl.canSaveAsTemplate && (
                  <LinkButton
                    label={t("dBot.project.save-as-template.title")}
                    onClick={ctrl.openSaveAsTemplateDialog}
                  />
                )}
                <ChangePermissionDialogButton
                  numberOfpermissions={ctrl.project.numberOfPermissions}
                  onClick={() => ctrl.dispatch({ type: "dbot/open-change-permission-dialog" })}
                />
                <PreviewMeetingLink ctrl={ctrl} />
                <DropdownMenu
                  alignment={"end"}
                  button={t("options")}
                  items={getProjectMenuItems(ctrl.summaryController, ctrl.dispatch, ctrl.name, t)}
                />
              </Box>
            ),
            onGoBack: ctrl.close,
            onGoBackVariant: "x",
          }
        }
        leftPanelContainerProps={{ className: "sm:basis-160 sm:shrink-0 relative" }}
        leftPanel={
          <>
            {ctrl.detailsPanel && (
              <VBox grow overflow={"hidden"} className={"absolute inset-0"}>
                <ProjectDetailsPanel panel={ctrl.detailsPanel} ctrl={ctrl} />
              </VBox>
            )}
            <VBox
              gap={"sm"}
              grow
              overflow={"hidden"}
              className={ctrl.detailsPanel && "invisible"}
              paddingBottom={"lg"}
            >
              {ctrl.visibleTabs.length > 1 && (
                <Box paddingX testId={"tab-panels"}>
                  <Tabs
                    tabs={ctrl.visibleTabs.map((tab) => ({
                      id: tab,
                      label: t(getTabLabel(tab)),
                      tag:
                        tab === "follow-up"
                          ? getActiveFollowUpPanels(
                              ctrl.summaryController.decisionsForApply,
                              ctrl.projectContext.locale,
                            )
                          : undefined,
                      isActive: ctrl.activeTab === tab,
                      onSelect: () => ctrl.dispatch({ type: "dbot/open-tab", tab: tab }),
                    }))}
                    scrollable={false}
                  />
                </Box>
              )}
              <VBox grow overflow={"auto"} paddingBottom={"lg"}>
                {ctrl.activeTab === "matters" && <ProjectAgenda ctrl={ctrl} />}
                {ctrl.activeTab === "documents" && <ProjectDocuments ctrl={ctrl} />}
                {ctrl.activeTab === "meetings" && <ProjectMeetings ctrl={ctrl} />}
                {ctrl.activeTab === "follow-up" && <ProjectFollowUp ctrl={ctrl} />}
                {ctrl.activeTab === "companies" && <ProjectCompanies ctrl={ctrl} />}
              </VBox>
            </VBox>
          </>
        }
        centerPanel={
          <HiddenForMobile>
            <DocumentTabs ctrl={ctrl} />
          </HiddenForMobile>
        }
      />

      <VisibleOnMobile>
        <ShowPreviewButton
          onClick={() => ctrl.dispatch({ type: "dbot/open-mobile-document-preview" })}
        />
        <MobileDocumentPreview ctrl={ctrl} />
      </VisibleOnMobile>

      <ProjectDialogs ctrl={ctrl} />
    </>
  );
});

const PreviewMeetingLink = observer(function PreviewMeetingLink({
  ctrl,
}: {
  ctrl: ProjectController;
}) {
  const { t } = useTranslation();
  if (isMobileView()) {
    return null;
  }
  switch (true) {
    case ctrl.containsNoticeGeneralMeeting && ctrl.containsNoticeBoardMeeting:
      return (
        <DropdownMenu
          button={{ type: "link", label: { key: "dbot.meeting.view" } }}
          items={[
            {
              type: "item",
              label: t("dbot.meeting.view.board-meeting"),
              icon: "calendar",
              onSelect: () =>
                openLinkInNewWindow(previewBoardMeetingForAdmin(ctrl.orgNo, ctrl.projectId)),
            },
            {
              type: "item",
              label: t("dbot.meeting.view.general-meeting"),
              icon: "calendar",
              onSelect: () =>
                openLinkInNewWindow(previewGeneralMeetingForAdmin(ctrl.orgNo, ctrl.projectId)),
            },
          ]}
        />
      );
    case ctrl.containsNoticeGeneralMeeting:
      return (
        <LinkButton
          label={{ key: "dbot.meeting.view" }}
          icon={"eye"}
          onClick={() =>
            openLinkInNewWindow(previewGeneralMeetingForAdmin(ctrl.orgNo, ctrl.projectId))
          }
        />
      );
    case ctrl.containsNoticeBoardMeeting:
      return (
        <LinkButton
          label={{ key: "dbot.meeting.view" }}
          icon={"eye"}
          onClick={() =>
            openLinkInNewWindow(previewBoardMeetingForAdmin(ctrl.orgNo, ctrl.projectId))
          }
        />
      );
    default:
      return null;
  }
});
